import React, { useState, useRef, useEffect, useCallback } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────

export interface TemLayer {
  id:               string;
  fileName:         string;
  imageDataUrl:     string;
  thumbnailDataUrl: string;
  translateX:       number;
  translateY:       number;
  rotate:           number;
  baseScale:        number;
  scale:            number;
}

interface Props {
  layers:          TemLayer[];
  longSidePx:      number;
  onChange:        (layers: TemLayer[]) => void;
  onUseAsTemplate: (dataUrl: string) => void;
  onClose:         () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────

let _idSeq = 0;
const genId = () => `tl_${++_idSeq}_${Date.now()}`;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload  = e => res(e.target!.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function makeThumbnail(src: string, thumbSize = 64): Promise<{ thumb: string; w: number; h: number }> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = c.height = thumbSize;
      const ctx = c.getContext('2d')!;
      const s = Math.min(thumbSize / img.width, thumbSize / img.height);
      const dw = img.width * s, dh = img.height * s;
      ctx.drawImage(img, (thumbSize - dw) / 2, (thumbSize - dh) / 2, dw, dh);
      res({ thumb: c.toDataURL(), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = rej;
    img.src = src;
  });
}

/** Klasyfikuj piksel: 0=czarny, 1=biały, 2=czerwony */
function classifyPixel(r: number, g: number, b: number, a: number): 0 | 1 | 2 {
  if (a < 128) return 0;
  if (r >= 128 && g < 100 && b < 100) return 2;
  if (r >= 128) return 1;
  return 0;
}

// ── Transform definitions ─────────────────────────────────────────────────

type TKey = 'translateX' | 'translateY' | 'rotate' | 'scale';

const TDEFS: { key: TKey; label: string; min: number; max: number; step: number; btnStep: number }[] = [
  { key: 'translateX', label: 'Translate X', min: -300, max: 300, step: 1,    btnStep: 1    },
  { key: 'translateY', label: 'Translate Y', min: -300, max: 300, step: 1,    btnStep: 1    },
  { key: 'rotate',     label: 'Rotate',      min: -180, max: 180, step: 0.5,  btnStep: 1    },
  { key: 'scale',      label: 'Scale',       min: 0.05, max: 5.0, step: 0.01, btnStep: 0.05 },
];

const NEUTRAL: Record<TKey, number> = {
  translateX: 0, translateY: 0, rotate: 0, scale: 1.0,
};

const CANVAS_SIZE = 325; // 260 * 1.25

// ── Composition ───────────────────────────────────────────────────────────

/**
 * Skomponuj piksele z kilku rastorów metodą: biały > czerwony (wszyscy) > czarny.
 * Jeśli highlightActiveIdx != null, aktywna warstwa jest wyświetlana z wyróżnionymi kolorami
 * zamiast normalnych, a pozostałe warstwy tworzą bazę.
 */
function composePixels(
  offscreens:        ImageData[],
  size:              number,
  highlightActiveIdx: number | null,
): ImageData {
  const result = new ImageData(size, size);
  const n = size * size;

  const activeData = (highlightActiveIdx !== null && highlightActiveIdx < offscreens.length)
    ? offscreens[highlightActiveIdx] : null;
  const others = (highlightActiveIdx !== null)
    ? offscreens.filter((_, i) => i !== highlightActiveIdx)
    : offscreens;

  for (let p = 0; p < n; p++) {
    const idx = p * 4;
    let r = 0, g = 0, b = 0;

    // Baza: kompozycja pozostałych warstw
    if (others.length > 0) {
      const cls = others.map(ip =>
        classifyPixel(ip.data[idx], ip.data[idx + 1], ip.data[idx + 2], ip.data[idx + 3]),
      );
      if (cls.some(c => c === 1)) {
        r = 255; g = 255; b = 255;                    // biały
      } else if (cls.every(c => c === 2)) {
        r = 255; g = 0;   b = 0;                      // czerwony (wszyscy)
      }
      // else czarny (0,0,0)
    }

    // Aktywna warstwa z podświetleniem
    if (activeData !== null) {
      const aAlpha = activeData.data[idx + 3];
      if (aAlpha >= 128) {
        const ac = classifyPixel(
          activeData.data[idx], activeData.data[idx + 1], activeData.data[idx + 2], aAlpha,
        );
        if (ac === 1) { r = 255; g = 255; b = 160; }  // blado-żółty
        else if (ac === 2) { r = 255; g = 80;  b = 80; }  // jasnoczerwony
        else              { r = 25;  g = 25;  b = 25; }   // ciemnoszary
      }
    } else if (activeData === null && highlightActiveIdx === null) {
      // Tryb normalny – rekompozycja bez podświetlenia (already done above via `others === offscreens`)
    }

    result.data[idx]     = r;
    result.data[idx + 1] = g;
    result.data[idx + 2] = b;
    result.data[idx + 3] = 255;
  }
  return result;
}

// ── Component ─────────────────────────────────────────────────────────────

const TemLayersDialog: React.FC<Props> = ({ layers, longSidePx, onChange, onUseAsTemplate, onClose }) => {
  const [activeIdx,       setActiveIdx]       = useState<number | null>(layers.length > 0 ? 0 : null);
  const [highlightActive, setHighlightActive] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileRef   = useRef<HTMLInputElement>(null);
  const imgCache  = useRef<Map<string, HTMLImageElement>>(new Map());

  const active = (activeIdx !== null && activeIdx < layers.length) ? layers[activeIdx] : null;

  // ── Image loading ────────────────────────────────────────────────────

  const loadImg = useCallback((src: string): Promise<HTMLImageElement> => {
    const cached = imgCache.current.get(src);
    if (cached) return Promise.resolve(cached);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload  = () => { imgCache.current.set(src, img); resolve(img); };
      img.onerror = () => reject(new Error('load error'));
      img.src = src;
    });
  }, []);

  // ── Render single layer to offscreen canvas ──────────────────────────

  const renderLayer = useCallback(async (l: TemLayer, size: number): Promise<ImageData> => {
    const img = await loadImg(l.imageDataUrl);
    const off = document.createElement('canvas');
    off.width = off.height = size;
    const octx = off.getContext('2d')!;
    const scale = size / CANVAS_SIZE;
    octx.save();
    octx.translate(size / 2 + l.translateX * scale, size / 2 + l.translateY * scale);
    octx.rotate((l.rotate * Math.PI) / 180);
    const dw = img.naturalWidth  * l.baseScale * l.scale * scale;
    const dh = img.naturalHeight * l.baseScale * l.scale * scale;
    octx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    octx.restore();
    return octx.getImageData(0, 0, size, size);
  }, [loadImg]);

  // ── Draw preview canvas ──────────────────────────────────────────────

  const drawCanvas = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (layers.length === 0) {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      return;
    }

    try {
      const offscreens = await Promise.all(layers.map(l => renderLayer(l, CANVAS_SIZE)));
      const hlIdx = (highlightActive && activeIdx !== null) ? activeIdx : null;
      const result = composePixels(offscreens, CANVAS_SIZE, hlIdx);
      ctx.putImageData(result, 0, 0);
    } catch { /* ignore */ }
  }, [layers, activeIdx, highlightActive, renderLayer]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  // ── Layer mutations ──────────────────────────────────────────────────

  const updateLayer = (idx: number, patch: Partial<TemLayer>) => {
    const next = [...layers];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const moveLayer = (idx: number, dir: -1 | 1) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= layers.length) return;
    const next = [...layers];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    onChange(next);
    setActiveIdx(ni);
  };

  // ── Event handlers ───────────────────────────────────────────────────

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const added: TemLayer[] = [];
    for (const file of Array.from(files)) {
      const dataUrl = await readFileAsDataUrl(file);
      const { thumb, w, h } = await makeThumbnail(dataUrl);
      const baseScale = Math.min(CANVAS_SIZE / w, CANVAS_SIZE / h);
      added.push({
        id: genId(), fileName: file.name,
        imageDataUrl: dataUrl, thumbnailDataUrl: thumb,
        translateX: 0, translateY: 0, rotate: 0, baseScale, scale: 1.0,
      });
    }
    onChange([...added, ...layers]);
    setActiveIdx(0);
    e.target.value = '';
  };

  const handleDelete = () => {
    if (activeIdx === null) return;
    const next = layers.filter((_, i) => i !== activeIdx);
    onChange(next);
    setActiveIdx(next.length > 0 ? Math.min(activeIdx, next.length - 1) : null);
  };

  const buildComposedDataUrl = async (): Promise<string> => {
    const size = longSidePx;
    const offscreens = await Promise.all(layers.map(l => renderLayer(l, size)));
    const result = composePixels(offscreens, size, null);
    const off = document.createElement('canvas');
    off.width = off.height = size;
    off.getContext('2d')!.putImageData(result, 0, 0);
    return off.toDataURL('image/png');
  };

  const handleSavePng = async () => {
    try {
      const dataUrl = await buildComposedDataUrl();
      const a = document.createElement('a');
      a.download = 'template.png';
      a.href = dataUrl;
      a.click();
    } catch (err: any) {
      alert(`Błąd zapisu PNG: ${err.message}`);
    }
  };

  const handleUseAsTemplate = async () => {
    try {
      const dataUrl = await buildComposedDataUrl();
      onUseAsTemplate(dataUrl);
    } catch (err: any) {
      alert(`Błąd generowania szablonu: ${err.message}`);
    }
  };

  const handleTransform = (key: TKey, raw: number) => {
    if (activeIdx === null) return;
    const def = TDEFS.find(d => d.key === key)!;
    updateLayer(activeIdx, { [key]: Math.max(def.min, Math.min(def.max, raw)) });
  };

  const handleStep = (key: TKey, dir: -1 | 1) => {
    if (!active) return;
    const def = TDEFS.find(d => d.key === key)!;
    handleTransform(key, (active[key] as number) + dir * def.btnStep);
  };

  const handleReset = (key: TKey) => {
    if (activeIdx === null) return;
    updateLayer(activeIdx, { [key]: NEUTRAL[key] });
  };

  // ── Transform rows ───────────────────────────────────────────────────

  const renderTransformRows = () => TDEFS.map(def => {
    const val = active ? (active[def.key] as number) : 0;
    const lbl = def.key === 'scale' ? `Scale ${val.toFixed(2)}` : def.label;
    const isNeutral = val === NEUTRAL[def.key];
    return (
      <div key={def.key} className="tr-row">
        <span className="tr-label">{lbl}</span>
        <div className="tr-controls">
          <button className="btn-pm" disabled={!active}
            onClick={() => handleStep(def.key, -1)}>−</button>
          <input type="range" className="tr-slider"
            min={def.min} max={def.max} step={def.step}
            value={val} disabled={!active}
            onChange={e => handleTransform(def.key, +e.target.value)} />
          <button className="btn-pm" disabled={!active}
            onClick={() => handleStep(def.key, 1)}>+</button>
          <button className="btn-reset" disabled={!active || isNeutral}
            title={`Reset to ${NEUTRAL[def.key]}`}
            onClick={() => handleReset(def.key)}>↺</button>
          <input type="number" className="tr-num"
            value={val.toFixed(2)} disabled={!active} step={def.step}
            onChange={e => handleTransform(def.key, +e.target.value)} />
        </div>
      </div>
    );
  });

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="layers-overlay">
      <div className="layers-dialog tem-layers-dialog">

        {/* ── Lewa kolumna: przyciski ── */}
        <div className="layers-left">
          <button onClick={() => fileRef.current?.click()}>Load PNG</button>
          <button onClick={handleDelete} disabled={activeIdx === null}>Delete</button>
          <button
            onClick={() => activeIdx !== null && moveLayer(activeIdx, -1)}
            disabled={activeIdx === null || activeIdx === 0}>Move Up</button>
          <button
            onClick={() => activeIdx !== null && moveLayer(activeIdx, 1)}
            disabled={activeIdx === null || activeIdx === layers.length - 1}>Move Down</button>
          <button onClick={handleSavePng}>Save PNG</button>
          <label className="tem-highlight-chk">
            <input
              type="checkbox"
              checked={highlightActive}
              onChange={e => setHighlightActive(e.target.checked)}
            />
            Highlight active
          </label>
          <div className="lyr-spacer" />
          <button onClick={handleUseAsTemplate}>Use as templ and close</button>
          <button onClick={onClose}>Close</button>
          <input ref={fileRef} type="file" accept="image/png" multiple
            style={{ display: 'none' }} onChange={handleFileChange} />
        </div>

        {/* ── Środkowa kolumna: podgląd + transformacje ── */}
        <div className="layers-center tem-layers-center">
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="layers-canvas"
          />
          <div className="layers-transforms">
            {renderTransformRows()}
          </div>
        </div>

        {/* ── Prawa kolumna: lista szablonów ── */}
        <div className="layers-right">
          <div className="lyr-list-head">
            <span className="lc-thumb">Thumbnail</span>
            <span className="lc-fname">File Name</span>
            <span className="lc-act">Actions</span>
          </div>
          <div className="lyr-list-body">
            {layers.map((layer, idx) => (
              <div
                key={layer.id}
                className={`lyr-row ${idx === activeIdx ? 'lyr-active' : ''}`}
                onClick={() => setActiveIdx(idx)}
              >
                <span className="lc-thumb">
                  <img src={layer.thumbnailDataUrl} alt="" className="lyr-thumb-img" />
                </span>
                <span className="lc-fname" title={layer.fileName}>{layer.fileName}</span>
                <span className="lc-act">
                  <button className="btn-arr" disabled={idx === 0}
                    onClick={e => { e.stopPropagation(); moveLayer(idx, -1); }}>↑</button>
                  <button className="btn-arr" disabled={idx === layers.length - 1}
                    onClick={e => { e.stopPropagation(); moveLayer(idx, 1); }}>↓</button>
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default TemLayersDialog;
